import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, Building2 } from "lucide-react";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/contexts/AuthContext";
import CustomerMasterTab from "@/components/CustomerMasterTab";

interface CompanyOption {
  id: string;
  name: string;
}

const CustomerMasterPage = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [companyName, setCompanyName] = useState("");
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(!companyId);

  useEffect(() => {
    if (!companyId) {
      setCompaniesLoading(true);
      const loadCompanies = async () => {
        if (!user?.id) {
          setCompaniesLoading(false);
          return;
        }
        const { data: access, error: accessError } = await supabase
          .from("user_company_access")
          .select("company_id")
          .eq("user_id", user.id);
        if (accessError || !access || access.length === 0) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("organization_id")
            .eq("id", user.id)
            .maybeSingle();
          const orgId = (profile as { organization_id?: string } | null)?.organization_id;
          if (orgId) {
            const { data: orgCompanies } = await supabase
              .from("company_masters")
              .select("id, name")
              .eq("organization_id", orgId)
              .order("name");
            setCompanies((orgCompanies || []).map((c: any) => ({ id: c.id, name: c.name })));
          }
        } else {
          const ids = access.map((a) => a.company_id).filter(Boolean);
          const { data: companiesData } = await supabase
            .from("company_masters")
            .select("id, name")
            .in("id", ids)
            .order("name");
          setCompanies((companiesData || []).map((c: any) => ({ id: c.id, name: c.name })));
        }
        setCompaniesLoading(false);
      };
      loadCompanies();
    }
  }, [companyId, user?.id]);

  useEffect(() => {
    if (!companyId) return;
    supabase.from("company_masters").select("name").eq("id", companyId).single()
      .then(({ data }) => { if (data) setCompanyName(data.name); });
  }, [companyId]);

  if (!companyId) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-primary">
          <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
            <Link to="/hub" className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--brand-green))]">
              <FileSpreadsheet className="h-4 w-4 text-white" />
            </Link>
            <div className="flex-1">
              <h1 className="text-base font-bold text-primary-foreground">Maestro de clientes</h1>
              <p className="text-xs text-primary-foreground/60">Selecciona la empresa para ver sus clientes</p>
            </div>
          </div>
        </header>
        <main className="mx-auto max-w-6xl px-6 py-8">
          <div className="rounded-lg border bg-card p-6 max-w-xl">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-brand" />
              Empresa
            </h2>
            {companiesLoading ? (
              <p className="text-sm text-muted-foreground">Cargando empresas...</p>
            ) : companies.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tienes empresas asignadas.</p>
            ) : (
              <div className="space-y-2">
                {companies.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/proveedores/${c.id}`)}
                    className="w-full text-left px-4 py-3 rounded-lg border hover:bg-accent hover:border-brand transition-colors"
                  >
                    <p className="font-medium text-sm">{c.name}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-primary">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
          <Link to={`/proveedores/${companyId}`} className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--brand-green))]">
            <FileSpreadsheet className="h-4 w-4 text-white" />
          </Link>
          <div className="flex-1">
            <h1 className="text-base font-bold text-primary-foreground">Maestro clientes · {companyName || "—"}</h1>
            <p className="text-xs text-primary-foreground/60">Gestión del listado de clientes para facturas emitidas</p>
          </div>
          <BackButton to={`/proveedores/${companyId}`} />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <CustomerMasterTab companyId={companyId} />
      </main>
    </div>
  );
};

export default CustomerMasterPage;
